import React, { createContext, ReactNode, useEffect, useRef } from 'react';
import { INchanSubscriber } from 'nchan';
import { isNumber } from 'lodash';
import axios from 'axios';
import { useDidUpdate } from 'rooks';

import {
  addNotificationMessage,
  getNotificationStreamConfig,
} from 'redux/reducers/notificationSlice';
import useSocket from 'hooks/useSocket';
import { SocketEvents, ERROR_RECONNECTION_TIME } from 'utils/socketUtils';
import {
  isAdminSelector,
  isAuthenticatedSelector,
} from 'redux/selectors/authenticationSelectors';
import { notificationStreamConfigSelector } from 'redux/selectors/notificationSelectors';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

export const NotificationSocketContext = createContext<INchanSubscriber | null>(
  null,
);

type Props = {
  children: ReactNode;
};

export default function NotificationSocketProvider(props: Props) {
  const { children } = props;
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(isAuthenticatedSelector);
  const errorIntervalRef = useRef<number>();
  const notificationStreamConfig = useAppSelector(
    notificationStreamConfigSelector,
  );
  const isAdmin = useAppSelector(isAdminSelector);
  const [socket, connect, stopSocket] = useSocket(
    notificationStreamConfig?.connectionUrl || '',
  );

  const reconnectSocket = () => {
    reset();
    dispatch(getNotificationStreamConfig());
  };

  useDidUpdate(() => {
    reconnectSocket();
  }, [isAdmin]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getNotificationStreamConfig());
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (!socket) return;

    socket.on(SocketEvents.Connect, () => {
      resetInterval();
    });

    socket.on(SocketEvents.Error, async () => {
      // Do not handle error if timer is already running
      if (isNumber(errorIntervalRef.current)) {
        return;
      }

      onError();
    });

    socket.on(SocketEvents.Message, onNewMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  useEffect(() => {
    return () => reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNewMessage = (message: string) => {
    dispatch(addNotificationMessage(message));
  };

  const onError = () => {
    stopSocket();

    errorIntervalRef.current = window.setInterval(async () => {
      try {
        await axios({
          method: 'get',
          url: notificationStreamConfig?.streamStatusUrl,
        });

        connect();
      } catch (error: any) {
        switch (error?.response?.status) {
          case 401: // Token is expired
          case 410: // Connection url is expired
          case 419: // Token and connection url are expired
            dispatch(getNotificationStreamConfig());
            break;

          // Session is expired
          case 404:
            reset();
            break;

          default:
            break;
        }
      }
    }, ERROR_RECONNECTION_TIME);
  };

  const resetInterval = () => {
    if (!errorIntervalRef.current) return;

    clearInterval(errorIntervalRef.current);
    errorIntervalRef.current = undefined;
  };

  const reset = () => {
    stopSocket();
    resetInterval();
  };

  return (
    <NotificationSocketContext.Provider value={socket}>
      {children}
    </NotificationSocketContext.Provider>
  );
}
